import { X } from 'lucide-react'
import { type ReactNode, useEffect } from 'react'
import { IconButton } from '../IconButton'
import styles from './Drawer.module.css'

type DrawerProps = {
  ariaLabelledBy?: string
  backdropClassName?: string | undefined
  children: ReactNode
  className?: string | undefined
  closeLabel?: string
  headerClassName?: string | undefined
  isDismissDisabled?: boolean
  onClose: () => void
  subtitle?: ReactNode
  title?: ReactNode
}

export const Drawer = ({
  ariaLabelledBy,
  backdropClassName,
  children,
  className,
  closeLabel = 'Close',
  headerClassName,
  isDismissDisabled = false,
  onClose,
  subtitle,
  title
}: DrawerProps) => {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && !isDismissDisabled) {
        onClose()
      }
    }

    document.addEventListener('keydown', onKeyDown)

    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isDismissDisabled, onClose])

  return (
    <div
      className={[styles.backdrop, backdropClassName].filter(Boolean).join(' ')}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isDismissDisabled) {
          onClose()
        }
      }}
    >
      <aside
        aria-labelledby={ariaLabelledBy}
        aria-modal="true"
        className={[styles.drawer, className].filter(Boolean).join(' ')}
        role="dialog"
      >
        {title !== undefined && (
          <div className={[styles.header, headerClassName].filter(Boolean).join(' ')}>
            <div>
              {title}
              {subtitle !== undefined && <p>{subtitle}</p>}
            </div>
            <IconButton
              disabled={isDismissDisabled}
              icon={<X size={17} />}
              label={closeLabel}
              onClick={onClose}
            />
          </div>
        )}
        {children}
      </aside>
    </div>
  )
}
